import { useEffect, useState } from 'react';

import { useLingui } from '@lingui/react/macro';
import { Button, Toast } from '@repo/videa-components/src/index';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { Input } from '@repo/videa-components/src/ui/input';
import { toast } from 'sonner';

import { useAddSuggestedValue } from '@/react-query/videa-notes/useAddSuggestedValue';

import { formatFieldKeyForDisplay } from './fieldDisplayHelpers';

export interface AddFieldValueModalProps {
  fieldKey: string;
  fieldLabel?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (suggestedValues: string[]) => void;
}

export const AddFieldValueModal = ({
  fieldKey,
  fieldLabel,
  isOpen,
  onOpenChange,
  onSuccess,
}: AddFieldValueModalProps) => {
  const [value, setValue] = useState('');

  const { t } = useLingui();
  const addSuggestedValue = useAddSuggestedValue();

  const fieldName = fieldLabel ?? formatFieldKeyForDisplay(fieldKey);
  const isSaveDisabled = !value.trim() || addSuggestedValue.isLoading;

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    try {
      const data = await addSuggestedValue.mutateAsync({
        fieldKey,
        value: trimmed,
      });
      onSuccess(data.suggestedValues);
      onOpenChange(false);
    } catch {
      toast(
        <Toast
          variant="error"
          title={t`Failed to add value`}
          description={t`There was an error adding the value. Please try again.`}
        />,
        {
          closeButton: true,
          position: 'top-right',
          style: { width: '460px' },
        },
      );
    }
  };

  useEffect(() => {
    if (!isOpen) setValue('');
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="vai-max-w-md">
        <DialogHeader>
          <DialogTitle>{t`Add new field value`}</DialogTitle>
        </DialogHeader>
        <div className="vai-flex vai-flex-col vai-gap-4">
          <div className="vai-flex vai-flex-col vai-gap-2">
            <label
              htmlFor="add-field-value-input"
              className="vai-text-sm vai-font-medium"
            >
              {t`Value Label`}
            </label>
            <Input
              id="add-field-value-input"
              placeholder={t`Insert label`}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (value.trim()) handleSave();
                }
              }}
              disabled={addSuggestedValue.isLoading}
              className="vai-w-full"
            />
          </div>
          <div className="vai-rounded-md vai-border vai-border-blue-200 vai-bg-blue-50 vai-p-3 vai-text-sm vai-text-blue-800">
            {t`This value will be available for selection in all templates using the`}{' '}
            <span className="vai-font-semibold">{fieldName}</span> {t`field.`}
          </div>
        </div>
        <DialogFooter className="vai-gap-2 sm:vai-justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={addSuggestedValue.isLoading}
          >
            {t`Cancel`}
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            {addSuggestedValue.isLoading ? t`Saving...` : t`Save`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
